import { Component, ElementRef, Injector, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { last, tap } from 'rxjs/operators';

import { Auth, AuthService } from '../../services/auth/auth.service';
import { BannerMessage, BannerMessageType } from '../../models/banner-message.model';
import { BannerMessageService } from '../../services/banner-message/banner-message.service';
import { CookiesComponent } from '../cookies/cookies.component';
import { CriteresConsultationPersonne } from '../../models/criteres-consultation-personne.model';
import { environment } from '../../../environments/environment';
import { HabilitationDto, HabilitationLiberalDto } from '../../models/habilitation-dto.model';
import { HabilitationSgcService } from '../../services/api/habilitation-sgc.service';
import { LoaderService } from '../../services/loader/loader.service';
import { messageContentEnum } from '../../models/message-content-enum.model';
import { MessageService } from '../../services/message/message.service';
import { PersonnePhysiqueService } from '../../services/personne-physique/personne-physique.service';
import { RassService } from '../../services/api/rass.service';
import { StructureDto } from '../../models/structure-dto.model';
import { StructureService } from '../../services/api/structure.service';
import { UserInfoService } from '../../services/user-info/user-info.service';

@Component({
  selector: 'app-connexion',
  templateUrl: './connexion.component.html',
  styleUrls: ['./connexion.component.scss']
})
export class ConnexionComponent implements OnInit {
  @ViewChild('cookiesComponent') cookiesComponent: CookiesComponent;
  @ViewChild('pscButton') pscButton: ElementRef;

  readonly CONNECTION_ABOUT_URL = environment.CONNECTION_ABOUT_URL;
  readonly CONNECTION_HELP_CART_URL = environment.CONNECTION_HELP_CART_URL;
  readonly CONNECTION_HELP_ECPS_URL = environment.CONNECTION_HELP_ECPS_URL;
  userInfo: UserInfoService;
  loaderService: LoaderService;
  rassService: RassService;
  personnePhysiqueService: PersonnePhysiqueService;
  habLiberales: HabilitationLiberalDto[] = [];
  public storage: Storage = sessionStorage; /* <--- you may switch between sessionStorage or LocalStrage (only one place to change)*/

  isCookiesAccepted;
  psc = false;
  loadingLogin = false;

  bannerMessages: Array<BannerMessage>;
  bannerMessageType = BannerMessageType;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly habilitationSgcServices: HabilitationSgcService,
    private readonly messageService: MessageService,
    private readonly structureService: StructureService,
    private readonly injector: Injector,
    private readonly bannerMessageService: BannerMessageService,
  ) {
    this.userInfo = this.injector.get<UserInfoService>(UserInfoService);
    this.rassService = this.injector.get<RassService>(RassService);
    this.loaderService = this.injector.get<LoaderService>(LoaderService);
    this.personnePhysiqueService = this.injector.get<PersonnePhysiqueService>(PersonnePhysiqueService);
  }

  ngOnInit(): void {
    this.isCookiesAccepted = this.getCookiesAccepted();
    this.loaderService.isLoading.subscribe((v) => this.loadingLogin = v);
    this.route.queryParams.subscribe((params) => {
      this.psc = params.psc ? params.psc : false;
    });
    this.goToHome();

    this.loadBannerMessages();
  }

  load() {
    if (this.getCookiesAccepted()) {
      this.loaderService.show();
      window.location.href = '/psc';
    }
  }

  goToHome() {
    if (this.psc) {
      this.loaderService.show();
      this.authService.getAuth().subscribe({
        next: (auth) => {
          if (auth.operationStatus === 'SUCCESS') {
            this.handleConnexionSuccess(auth);
          } else {
            this.loaderService.hide();
            this.messageService.error(messageContentEnum.ERROR_CONNEXION);
          }
        },
        error: () => {
          this.loaderService.hide();
          this.messageService.error(messageContentEnum.ERROR_CONNEXION);
        },
      });
    }
  }

  private handleConnexionSuccess(auth: Auth) {
    this.userInfo.storeAuth(auth);
    const criteres: CriteresConsultationPersonne = {
      idNatPs: auth.item.userId,
      isTest: 'false',
    };
    this.rassService.consultationPP(criteres).pipe(
      tap((personne) => {
        if (!personne) {
          throw new Error()
        }
      })).subscribe({
        next: (personne) => {
          this.userInfo.storePersonnes(personne);
          this.personnePhysiqueService
            .getPersonneFromIdNatPs(
              personne.typePersonne,
              personne.idPersonne
            )
            .subscribe((res) => {
              this.userInfo.storePersonnes({
                ...personne,
                email: res.email,
                numeroDeTel: res.numeroDeTel,
              });
            });
          this.habilitationSgcServices
            .getAllHabilitationsLiberales()
            .pipe(last())
            .subscribe((habLiberales: HabilitationLiberalDto[]) => {
              this.userInfo.storeHabilitations(habLiberales);
              if (auth.item?.roles.includes('VISITEUR')) {
                this.loaderService.hide();
                this.router.navigate(['/profile']);
              } else {
                this.router.navigate(['/home']);
                habLiberales = habLiberales?.filter(
                  (h) =>
                    h.profil === 'REPRESENTANT_LEGAL' ||
                    h.profil === 'MANDATAIRE' ||
                    h.profil === 'DELEGUE'
                );
                const listIdStructure = this.createListIdStructure(
                  habLiberales
                );
                this.structureService
                  .getByListIdStructure(listIdStructure)
                  .subscribe({
                    next: (str: Map<string, StructureDto>) => {
                      const habilitationLiberales: HabilitationDto[] = [];
                      habLiberales = this.asignStructureLiberaleToHab(str, habLiberales);
                      habLiberales.forEach((habLiberale, index) => {
                        const structure: StructureDto = str[habLiberale.structure.idNatStruct];
                        let habilitation = new HabilitationDto();
                        habilitation = {
                          ...habilitation,
                          idStructure: habLiberale.structure?.idNatStruct.slice(1),
                          etat: 'OUVERT',
                          personneHabilitee: JSON.parse(this.storage.getItem('personnes')),
                          roleMetier: {
                            code: habLiberale.profil,
                          },
                          id: index.toString(),
                          structure: {
                            abonne: structure?.abonne ?? false,
                            isLiberal: !!structure?.typeStructure?.includes('LIBERAL'),
                            typeStructure: habLiberale.structure?.idNatStruct.slice(0, 1),
                            idStructure: habLiberale.structure?.idNatStruct.slice(1),
                            numeroContrat: habLiberale.referenceContrat?.identifiant,
                            raisonSociale: habLiberale.structure?.raisonSociale,
                            statutRass: habLiberale.structure?.statutRass,
                            structureTiers: structure?.structureTiers,
                            typeEntite: structure?.typeEntite
                          },
                          cleContrat: habLiberale.structure?.cleContrat,
                          clePerimetreContractuel: habLiberale.structure?.clePerimetreContractuel,
                        };
                        habilitationLiberales.push(habilitation);
                      });
                      
                      this.userInfo.storeHabilitationsAuth(
                        habilitationLiberales
                      );
                      this.userInfo.pushStoredHabilitationsUpdatedEvent();
                      this.loaderService.hide();
                    },
                  });
              }
            });
        },
        error: () => {
          this.loaderService.hide();
          this.messageService.error(messageContentEnum.ERROR_CONNEXION);
        },
      });
  }

  createListClePermietreContractuel(
    habilitations: HabilitationDto[]
  ): string[] {
    const list: string[] = [];
    if (typeof habilitations[Symbol.iterator] === 'function') {
      for (const h of habilitations) {
        if (h.clePerimetreContractuel) {
          list.push(h.clePerimetreContractuel);
        }
      }
    }
    return list;
  }

  createListIdStructure(
    habilitationLiberales: HabilitationLiberalDto[]
  ): string[] {
    const list: string[] = [];
    if (typeof habilitationLiberales[Symbol.iterator] === 'function') {
      for (const h of habilitationLiberales) {
        if (h.structure?.idNatStruct) {
          list.push(h.structure?.idNatStruct);
        }
      }
    }
    return list;
  }

  asignStructureLiberaleToHab(
    structures: Map<string, StructureDto>,
    habilitations: HabilitationLiberalDto[]
  ): HabilitationLiberalDto[] {
    Object.keys(structures).forEach((keyStr: string) => {
      habilitations.forEach((hab) => {
        if (hab.structure?.idNatStruct === keyStr) {
          hab.structure.clePerimetreContractuel = structures[keyStr].clePerimetreContractuel;
          hab.structure.cleContrat = structures[keyStr].cleContrat;
          if (hab.referenceContrat) {
            hab.referenceContrat.identifiant = structures[keyStr].numeroContrat;
          }
        }
      });
    });
    return habilitations;
  }

  /*** Cookies ***/

  getCookiesAccepted() {
    return this.userInfo.getCookiesAccepted();
  }

  showCookiesPopin(): void {
    this.cookiesComponent.ngOnInit();
    this.cookiesComponent.ngAfterViewInit();
  }

  onCookiesAccepted(isAccepted: boolean) {
    this.isCookiesAccepted = isAccepted;
    setTimeout(() => this.pscButton.nativeElement.focus(), 500);
  }

  /*** Banner messages ***/

  private loadBannerMessages() {
    this.bannerMessageService.getBannerData().subscribe({
      next: (res) => this.bannerMessages = res,
      error: (error) => console.log('ERROR', error)
    });
  }

}
